import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  alpha,
  Card,
  CardContent,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { amberColors, numenorColors, rarityColors } from '../../theme/themeConfig';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BuildIcon from '@mui/icons-material/Build';

import AnalyticsIcon from '@mui/icons-material/Analytics';

export const DevelopersRoadmap: React.FC = () => {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const navigate = useNavigate();

  // Roadmap data
  const roadmapPhases = [
    {
      phase: 'Q1 2025',
      status: 'completed',
      title: 'Core Foundation',
      description:
        'Release core functionality including projects and estimation workflows, document management, AI chat assistant, and complete closeout workflow through to total pricing.',
      features: [
        { name: 'Project Management', status: 'completed', icon: CheckCircleIcon },
        { name: 'Document Management', status: 'completed', icon: CheckCircleIcon },
        { name: 'Estimation Workflows', status: 'completed', icon: CheckCircleIcon },
        { name: 'AI Chat Assistant', status: 'completed', icon: CheckCircleIcon },
        { name: 'Complete Closeout Workflow', status: 'completed', icon: CheckCircleIcon },
        { name: 'Total Pricing System', status: 'completed', icon: CheckCircleIcon },
      ],
    },
    {
      phase: 'Q2 2025',
      status: 'completed',
      title: 'Contract Management',
      description:
        'Release change order and contract management workflows, core functionality polish and improvements, AI report generators.',
      features: [
        { name: 'Change Order Management', status: 'completed', icon: CheckCircleIcon },
        { name: 'Contract Management', status: 'completed', icon: CheckCircleIcon },
        { name: 'Core Functionality Polish', status: 'completed', icon: CheckCircleIcon },
        { name: 'AI Report Generators', status: 'completed', icon: CheckCircleIcon },
      ],
    },
    {
      phase: 'Q3 2025',
      status: 'in-progress',
      title: 'MEP Items Database',
      description:
        'Release fully managed items database of MEP parts and their associated average market pricing. Begin populating the items database with production values.',
      features: [
        { name: 'MEP Items Database', status: 'in-progress', icon: AccessTimeIcon },
        { name: 'Market Pricing Integration', status: 'planned', icon: AnalyticsIcon },
        { name: 'Production Value Population', status: 'planned', icon: AccessTimeIcon },
        { name: 'Parts Catalog Management', status: 'planned', icon: BuildIcon },
      ],
    },
    {
      phase: 'Q4 2025',
      status: 'planned',
      title: 'Global Assemblies Database',
      description:
        'Release fully managed global assemblies database, enabling users to jump in to the software and start doing takeoff on day one! No more starting from scratch!',
      features: [
        { name: 'Global Assemblies Database', status: 'planned', icon: BuildIcon },
        { name: 'Day-One Takeoff Ready', status: 'planned', icon: CheckCircleIcon },
        { name: 'Pre-built Assembly Library', status: 'planned', icon: BuildIcon },
        { name: 'Zero Setup Estimation', status: 'planned', icon: CheckCircleIcon },
      ],
    },
  ];

  // Get status color and icon
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          color: rarityColors.uncommon,
          label: 'Completed',
          icon: CheckCircleIcon,
        };
      case 'in-progress':
        return {
          color: amberColors[600],
          label: 'In Progress',
          icon: AccessTimeIcon,
        };
      case 'planned':
        return {
          color: rarityColors.rare,
          label: 'Planned',
          icon: RadioButtonUncheckedIcon,
        };
      default:
        return {
          color: theme.palette.grey[500],
          label: 'Unknown',
          icon: RadioButtonUncheckedIcon,
        };
    }
  };

  // Background style consistent with other pages
  const backgroundStyle = darkMode
    ? {
        background: `
          linear-gradient(135deg, ${alpha('#111827', 0.85)} 0%, ${alpha('#1f2937', 0.85)} 50%, ${alpha('#111827', 0.85)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      }
    : {
        background: `
          linear-gradient(135deg, ${alpha(numenorColors[50], 0.78)} 0%, ${alpha(numenorColors[100], 0.78)} 50%, ${alpha(numenorColors[50], 0.78)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      };

  return (
    <>
      <Helmet>
        <title>Developer's Roadmap | Unity Build Estimation</title>
        <meta
          name="description"
          content="Explore the development roadmap for Unity Build Estimation and see what features are coming next to the platform."
        />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          ...backgroundStyle,
          position: 'relative',
          overflow: 'auto',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            pointerEvents: 'none',
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            py: { xs: 2, sm: 3, md: 6 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {/* Home Icon - Top Right */}
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16, md: 24 },
              right: { xs: 12, sm: 16, md: 24 },
              zIndex: 1000,
            }}
          >
            <Tooltip title="Back to Home" arrow>
              <IconButton
                onClick={() => navigate('/')}
                sx={{
                  bgcolor: alpha(darkMode ? '#1a1a1a' : '#ffffff', 0.9),
                  backdropFilter: 'blur(10px)',
                  border: 1,
                  borderColor: alpha(theme.palette.divider, 0.3),
                  color: amberColors[600],
                  minWidth: { xs: 40, sm: 44, md: 48 },
                  minHeight: { xs: 40, sm: 44, md: 48 },
                  '&:hover': {
                    bgcolor: alpha(darkMode ? '#2d2d2d' : '#f5f5f5', 0.95),
                    borderColor: amberColors[600],
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <HomeIcon fontSize={window.innerWidth < 600 ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Main Content Paper */}
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 3, md: 6 },
              backgroundColor: darkMode
                ? alpha(theme.palette.background.paper, 0.95)
                : alpha(theme.palette.background.paper, 0.98),
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: 1,
              borderColor: alpha(theme.palette.divider, 0.2),
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4, md: 6 } }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 2, md: 3 },
                  color: amberColors[600],
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                }}
              >
                Developer's Roadmap
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  px: { xs: 1, sm: 0 },
                }}
              >
                Core features planned, alongside ongoing polish and refinement. Built transparently,
                one feature at a time.
              </Typography>
            </Box>

            {/* Timeline */}
            <Box sx={{ position: 'relative' }}>
              {/* Vertical timeline line */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 12, sm: 20, md: 32 },
                  top: 0,
                  bottom: 0,
                  width: { xs: 1.5, md: 2 },
                  bgcolor: alpha(amberColors[600], 0.3),
                  zIndex: 1,
                }}
              />

              {roadmapPhases.map((phase, index) => {
                const statusConfig = getStatusConfig(phase.status);

                return (
                  <Box key={index} sx={{ position: 'relative', mb: { xs: 2, sm: 3, md: 4 } }}>
                    {/* Timeline dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: { xs: 6, sm: 14, md: 24 },
                        top: { xs: 16, sm: 20, md: 24 },
                        width: { xs: 14, md: 18 },
                        height: { xs: 14, md: 18 },
                        borderRadius: '50%',
                        bgcolor: statusConfig.color,
                        border: `${window.innerWidth < 600 ? 2 : 3}px solid ${alpha(statusConfig.color, 0.3)}`,
                        zIndex: 2,
                      }}
                    />

                    {/* Content */}
                    <Card
                      elevation={0}
                      sx={{
                        ml: { xs: 4, sm: 6, md: 8 },
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        border: 1,
                        borderColor: alpha(theme.palette.divider, 0.1),
                        '&:hover': {
                          bgcolor: alpha(statusConfig.color, 0.05),
                          borderColor: alpha(statusConfig.color, 0.2),
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                        {/* Phase header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: { xs: 1.5, md: 2 },
                            flexWrap: 'wrap',
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mr: { xs: 0, sm: 2 },
                              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                              flexGrow: { xs: 1, sm: 0 },
                            }}
                          >
                            {phase.phase}
                          </Typography>
                          <Chip
                            label={statusConfig.label}
                            size="small"
                            sx={{
                              bgcolor: statusConfig.color,
                              color: 'white',
                              fontWeight: 500,
                              fontSize: { xs: '0.7rem', md: '0.75rem' },
                              height: { xs: '24px', md: '28px' },
                            }}
                          />
                        </Box>

                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            mb: { xs: 1, md: 1 },
                            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                          }}
                        >
                          {phase.title}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            mb: { xs: 2, md: 3 },
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1rem' },
                            lineHeight: 1.6,
                          }}
                        >
                          {phase.description}
                        </Typography>

                        <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />

                        {/* Features */}
                        <Grid container spacing={{ xs: 1.5, md: 2 }}>
                          {phase.features.map((feature, featureIndex) => {
                            const featureStatus = getStatusConfig(feature.status);
                            const FeatureIcon = featureStatus.icon;

                            return (
                              <Grid item xs={12} sm={6} key={featureIndex}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <FeatureIcon
                                    sx={{
                                      color: featureStatus.color,
                                      mr: 1,
                                      fontSize: { xs: '1rem', md: '1.2rem' },
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                                    }}
                                  >
                                    {feature.name}
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>

            {/* Footer note */}
            <Box
              sx={{
                mt: { xs: 3, sm: 4, md: 6 },
                p: { xs: 2, sm: 2.5, md: 3 },
                bgcolor: alpha(amberColors[600], 0.1),
                borderRadius: 2,
                border: 1,
                borderColor: alpha(amberColors[600], 0.2),
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1rem' },
                }}
              >
                This roadmap is a living document
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                  fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                  lineHeight: 1.5,
                }}
              >
                Priorities may shift based on user feedback and market needs. Your input helps shape
                our development direction.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: amberColors[600],
                  fontWeight: 500,
                  fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.875rem' },
                }}
              >
                Please email phil@unitybuildestimation.com with thoughts / suggestions
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default DevelopersRoadmap;
